import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  FavoriteBorder,
  Favorite,
  Add,
  Remove,
  Star,
  LocalFireDepartment,
  Bolt,
} from "@mui/icons-material";
import { BranchProduct } from "../../types";
import { useProductContext } from "../../context/ProductContext";
import { useNotification } from "../../context/NotificationContext";
import { useTranslation } from "react-i18next";
import RatingStars from "./productCard/RatingStars";
import ExtraSmallProductCard from "./productCard/SmallProductCard";
import SmallProductCard1 from "./productCard/SmallProductCard1";
import CompactProductCard from "./productCard/CompactProductCard";
import ListProductCard from "./productCard/ListProductCard";
import AnimatedProductCard from "./productCard/ProductCardAnimated";

interface ProductCardProps {
  product: BranchProduct;
  variant?: "grid" | "list" | "compact";
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  variant = "grid",
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToCart } = useProductContext();
  const { notify } = useNotification();

  // States
  const [isWishlist, setIsWishlist] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [cardWidth, setCardWidth] = useState(0);

  // Product data
  const images = product.product.images || [];
  const maxQuantity = Math.min(product.stock || 10, 99);
  const rating = product.product.average_rating || 0;
  const badge = product.product.badge;
  const isFeatured = product.product.is_featured;

  // Responsive colors based on variant
  const getCardColors = useMemo(() => {
    const colors = [
      "from-pink-500 to-rose-400 hover:from-pink-600 hover:to-rose-500",
      "from-purple-500 to-violet-400 hover:from-purple-600 hover:to-violet-500",
      "from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500",
      "from-emerald-500 to-teal-400 hover:from-emerald-600 hover:to-teal-500",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }, []);

  // Image rotation
  useEffect(() => {
    if (isHovered && images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isHovered, images.length]);

  // Track card width for responsive adjustments
  useEffect(() => {
    const updateWidth = () => {
      const card = document.querySelector(`[data-product-id="${product.id}"]`);
      if (card) {
        setCardWidth(card.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [product.id]);

  // WhatsApp purchase function
  // بدل الدالة الحالية، استبدلها بهذا:

  // WhatsApp purchase function
  const handleWhatsAppPurchase = (qty: number) => {
    const message = encodeURIComponent(
      `مرحباً، أريد شراء المنتج التالي:\n\n` +
        `📦 المنتج: ${product.product.name}\n` +
        `💰 السعر: ج.م ${product.price}\n` +
        `🔢 الكمية: ${qty}\n` +
        `💰 الإجمالي: ج.م ${(product.price * qty).toFixed(2)}\n\n` +
        `يرجى تأكيد الطلب وشكراً!`
    );

    const phoneNumber = "201234567890"; // استبدل برقم الواتساب الخاص بك
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;

    // فتح واتساب
    window.open(whatsappUrl, "_blank");

    // تصفير العداد
    setQuantity(1);

    // إشعار بالنجاح
    notify("success", `تم فتح واتساب للشراء بكمية ${qty}`);
  };

  // Determine layout based on card width
  const getLayoutVariant = () => {
    if (cardWidth < 160) return "extra-small"; // Mobile - 2 columns
    if (cardWidth < 250) return "small"; // Mobile - 2 columns (compact)
    if (cardWidth < 350) return "medium"; // Tablet - 3 columns
    return "large"; // Desktop - 4+ columns
  };

  const layoutVariant = getLayoutVariant();

  // Handlers
  const handleAddToCart = (qty: number) => {
    for (let i = 0; i < qty; i++) {
      addToCart(product.product.id);
    }

    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 1500);

    notify(
      "success",
      t("productCard.addedToCart", {
        name: product.product.name,
        qty: qty,
      })
    );
  };

  const toggleWishlist = () => {
    setIsWishlist(!isWishlist);
    notify(
      "success",
      isWishlist
        ? t("productCard.removedFromWishlist", { name: product.product.name })
        : t("productCard.addedToWishlist", { name: product.product.name })
    );
  };

  // Helper functions
  const getStockStatus = () => {
    if (!product.is_available || product.stock <= 0) {
      return {
        text: t("productCard.outOfStock"),
        color: "bg-red-100 text-red-700",
      };
    }
    if (product.stock <= 3) {
      return {
        text: t("productCard.lowStock"),
        color: "bg-yellow-100 text-yellow-700",
      };
    }
    return {
      text: t("productCard.inStock"),
      color: "bg-green-100 text-green-700",
    };
  };

  const stockStatus = getStockStatus();

  const getBadgeColor = () => {
    const colors: Record<string, string> = {
      sale: "bg-red-500 text-white",
      new: "bg-blue-500 text-white",
      hot: "bg-orange-500 text-white",
      bestseller: "bg-purple-500 text-white",
      featured: "bg-gradient-to-r from-yellow-500 to-orange-500 text-white",
    };
    return colors[badge?.toLowerCase() || ""] || "bg-gray-500 text-white";
  };

  // Rating stars - responsive

  // WhatsApp Icon component
  const WhatsAppIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.507 14.307l-.009.075c-.016.15-.026.302-.026.456 0 2.757-2.243 5-5 5-.163 0-.323-.008-.482-.023l-.074-.009-4.187 1.521a.499.499 0 01-.646-.606l1.511-4.219c-2.346-1.336-3.897-3.776-3.897-6.494 0-4.142 3.358-7.5 7.5-7.5 4.142 0 7.5 3.358 7.5 7.5 0 2.943-1.693 5.494-4.162 6.743zm-5.507-12.307c-5.238 0-9.5 4.262-9.5 9.5 0 2.666 1.115 5.155 3.065 6.933l.137.116-1.081 3.175 3.158-1.149.116.067c1.725.963 3.648 1.458 5.605 1.458 5.238 0 9.5-4.262 9.5-9.5s-4.262-9.5-9.5-9.5z" />
    </svg>
  );

  // EXTRA SMALL LAYOUT - Mobile (width < 160px)
if (layoutVariant === "extra-small") {
  return (
    <ExtraSmallProductCard
      product={product}
      getCardColors={getCardColors}
      getBadgeColor={getBadgeColor}
      t={t}
      handleWhatsAppPurchase={handleWhatsAppPurchase}
    />
  );
}


  // SMALL LAYOUT - Mobile (160px < width < 250px)
  if (layoutVariant === "small") {
    return (
    <SmallProductCard1
      product={product}
      getCardColors={getCardColors}
      getBadgeColor={getBadgeColor}
      t={t}
      handleWhatsAppPurchase={handleWhatsAppPurchase}
    />
    );
  }

  // COMPACT VARIANT - Manual override
  if (variant === "compact") {
    return (
 <CompactProductCard
      product={product}
      getCardColors={getCardColors}
      t={t}
    />
    );
  }

  // LIST VARIANT
  if (variant === "list") {
    return (
      <ListProductCard 
        product={product}
        getCardColors={getCardColors}
        t={t}
        />
    );
  }

  // DEFAULT GRID VARIANT - للعرض العادي
  return (
    <>
     {/* استخدام المكون الجديد */}
      <AnimatedProductCard 
        product={product}
        isWishlist={isWishlist}
        quantity={quantity}
        currentImageIndex={currentImageIndex}
        isHovered={isHovered}
        images={images}
        maxQuantity={maxQuantity}
        rating={rating}
        badge={badge}
        isFeatured={isFeatured}
        stockStatus={stockStatus}
        getCardColors={getCardColors}
        getBadgeColor={getBadgeColor}
        layoutVariant={layoutVariant}
        t={t}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        toggleWishlist={toggleWishlist}
        handleAddToCart={handleAddToCart}
        handleWhatsAppPurchase={handleWhatsAppPurchase}
        setQuantity={setQuantity}
        setCurrentImageIndex={setCurrentImageIndex}
      />
      {/* Notification */}
      <AnimatePresence>
        {showNotification && (
          <motion.div
            className="fixed top-24 right-6 z-50 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-2 backdrop-blur-sm"
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 100, opacity: 0 }}
          >
            <ShoppingCart className="w-5 h-5" />
            <span className="font-medium">
              {t("productCard.addedToCart", { name: "", qty: quantity }) ||
                "تم الإضافة للسلة!"}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProductCard;
